import { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Spin, message, Button, Table, Tag, Breadcrumb, Progress,
} from 'antd';
import { DownloadOutlined, RightOutlined, HomeOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import { energy, reports } from '../../../services/api';

const PERIOD_COLORS = { 尖峰: '#ff4d4f', 高峰: '#fa8c16', 平段: '#1890ff', 低谷: '#52c41a' };

function touBarOption(tou) {
  if (!tou?.periods) return {};
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { data: ['用电量', '电费'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: tou.periods.map((p) => p.period) },
    yAxis: [
      { type: 'value', name: 'kWh' },
      { type: 'value', name: '元', splitLine: { show: false } },
    ],
    series: [
      {
        name: '用电量',
        type: 'bar',
        data: tou.periods.map((p) => ({
          value: p.consumption_kwh,
          itemStyle: { color: PERIOD_COLORS[p.period] },
        })),
      },
      {
        name: '电费',
        type: 'line',
        yAxisIndex: 1,
        data: tou.periods.map((p) => p.cost_yuan),
        itemStyle: { color: '#722ed1' },
      },
    ],
  };
}

function hourlyTouOption(tou) {
  if (!tou?.hourly) return {};
  const hours = tou.hourly.map((h) => h.hour);
  const data = tou.hourly.map((h) => ({
    value: h.consumption_kwh,
    itemStyle: { color: PERIOD_COLORS[h.period] },
  }));
  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const p = params[0];
        const h = tou.hourly[p.dataIndex];
        return `${h.hour}<br/>时段: ${h.period}<br/>用电: ${h.consumption_kwh} kWh<br/>电价: ${h.price} 元/kWh<br/>电费: ${h.cost_yuan} 元`;
      },
    },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: hours },
    yAxis: { type: 'value', name: 'kWh' },
    series: [{ name: '分时用电', type: 'bar', data }],
  };
}

function touPieOption(tou) {
  if (!tou?.periods) return {};
  return {
    tooltip: { trigger: 'item', formatter: '{b}: {c} kWh ({d}%)' },
    series: [{
      type: 'pie',
      radius: ['42%', '68%'],
      data: tou.periods.map((p) => ({
        name: p.period,
        value: p.consumption_kwh,
        itemStyle: { color: PERIOD_COLORS[p.period] },
      })),
      label: { formatter: '{b}\n{d}%' },
    }],
  };
}

export default function EnergyPage() {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);
  const [spaceId, setSpaceId] = useState(null);

  const loadData = useCallback(async (sid) => {
    setLoading(true);
    try {
      const res = await energy.analysis({ granularity: 'day', space_id: sid || undefined });
      setData(res.data);
      if (!sid && res.data?.hierarchy?.current_space_id) {
        setSpaceId(res.data.hierarchy.current_space_id);
      }
    } catch (err) {
      message.error(err.message || '加载能效数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(spaceId); }, [spaceId]);

  const handleDrill = (id) => setSpaceId(id);
  const handleBreadcrumb = (id) => setSpaceId(id);

  const handleExport = async () => {
    try {
      setExporting(true);
      const today = new Date().toISOString().split('T')[0];
      await reports.export({ type: 'daily', start_date: today, end_date: today });
      message.success('报表已导出');
    } catch (err) {
      message.error(err.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  if (!data) return <div>暂无数据</div>;

  // summary 在无数据时为 null（data_source: no_data）。注意：解构默认值只对 undefined 生效，
  // 对 null 不生效，因此 summary 必须用 || 兜底；tou/hierarchy 同理用 || 兜底 null。
  const { days = [], series = [] } = data;
  const summary = data.summary || {};
  const tou = data.tou || {};
  const hierarchy = data.hierarchy || { breadcrumb: [], children: [] };
  const consumptionSeries = series.find((s) => s.name === '总用电量');
  const pvSeries = series.find((s) => s.name === '光伏发电量');
  const storageSeries = series.find((s) => s.name === '储能放电量');

  const chartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: series.map((s) => s.name) },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: days.map((d) => d.slice(5)) },
    yAxis: { type: 'value', name: 'kWh' },
    series: series.map((s) => ({ name: s.name, type: 'bar', data: s.data })),
  };

  const totalPv = pvSeries?.data.reduce((a, b) => a + b, 0) || 0;
  const totalStorage = storageSeries?.data.reduce((a, b) => a + b, 0) || 0;
  const totalGrid = (consumptionSeries?.data.reduce((a, b) => a + b, 0) || 0) - totalPv - totalStorage;

  // 光伏自用/上网无真实计量数据，不再按 70/30 固定比例编造拆分
  const pieOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} kWh ({d}%)' },
    series: [{
      type: 'pie', radius: ['40%', '70%'],
      data: [
        { name: '市电购入', value: Math.max(0, Math.round(totalGrid)), itemStyle: { color: '#1890ff' } },
        { name: '光伏发电', value: Math.round(totalPv), itemStyle: { color: '#52c41a' } },
        { name: '储能放电', value: Math.round(totalStorage), itemStyle: { color: '#faad14' } },
      ],
      label: { formatter: '{b}\n{d}%' },
    }],
  };

  // 直接碳排放按电网平均排放因子 0.5703 kgCO₂/kWh 估算（估算口径，无更高精度数据来源）
  const co2Direct = Math.round((summary.total_consumption || 0) * 0.5703);
  const co2Reduction = Math.round(summary.co2_reduction || 0);

  const childColumns = [
    { title: '空间名称', dataIndex: 'name', render: (name, row) => (
      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => handleDrill(row.id)}>
        {name} {row.has_children && <RightOutlined style={{ fontSize: 10 }} />}
      </Button>
    )},
    { title: '层级', dataIndex: 'type', render: (t) => <Tag>{t}</Tag> },
    { title: '今日用电(kWh)', dataIndex: 'consumption_kwh', render: (v) => v.toLocaleString() },
    { title: '今日电费(元)', dataIndex: 'cost_yuan', render: (v) => <span style={{ color: '#fa8c16' }}>{v.toLocaleString()}</span> },
    { title: '均价(元/kWh)', dataIndex: 'avg_price', render: (v) => v.toFixed(4) },
    { title: '表计数', dataIndex: 'meter_count' },
    {
      title: '尖峰占比',
      dataIndex: 'period_breakdown',
      render: (breakdown) => {
        const peak = breakdown?.find((p) => p.period === '尖峰');
        return peak ? <Progress percent={peak.ratio} size="small" strokeColor="#ff4d4f" /> : '-';
      },
    },
  ];

  const touTableColumns = [
    { title: '时段', dataIndex: 'period', render: (p) => <Tag color={PERIOD_COLORS[p]}>{p}</Tag> },
    { title: '电价(元/kWh)', dataIndex: 'price', render: (v) => v.toFixed(4) },
    { title: '用电量(kWh)', dataIndex: 'consumption_kwh', render: (v) => v.toLocaleString() },
    { title: '占比', dataIndex: 'ratio', render: (v) => `${v}%` },
    { title: '电费(元)', dataIndex: 'cost_yuan', render: (v) => <b>{v.toLocaleString()}</b> },
  ];

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Breadcrumb
          items={[
            {
              title: (
                <span style={{ cursor: 'pointer' }} onClick={() => handleBreadcrumb(hierarchy.breadcrumb[0]?.id)}>
                  <HomeOutlined /> 全部园区
                </span>
              ),
            },
            ...hierarchy.breadcrumb.map((b, i) => ({
              title: i < hierarchy.breadcrumb.length - 1 ? (
                <span style={{ cursor: 'pointer' }} onClick={() => handleBreadcrumb(b.id)}>{b.name}</span>
              ) : b.name,
            })),
          ]}
        />
        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
          四级结构化用能监测：园区 → 楼宇/区域 → 配电回路 → 终端。点击子空间名称可下钻查看明细。
        </div>
      </Card>

      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="总用电量" value={summary.total_consumption} suffix="kWh" precision={0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="总电费" value={summary.total_cost_yuan} suffix="元" precision={0} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="综合均价" value={summary.avg_price} suffix="元/kWh" precision={4} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="自给率" value={summary.self_sufficiency_rate} suffix="%" precision={1} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={16}>
          <Card title="尖峰平谷分时统计（今日）" size="small">
            <ReactEChartsCore option={touBarOption(tou)} style={{ height: 260 }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="时段用电占比" size="small">
            <ReactEChartsCore option={touPieOption(tou)} style={{ height: 260 }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="24小时分时用电曲线（颜色区分尖峰平谷）" size="small">
            <div style={{ marginBottom: 8 }}>
              {Object.entries(PERIOD_COLORS).map(([k, c]) => (
                <Tag key={k} color={c} style={{ marginRight: 8 }}>{k}</Tag>
              ))}
            </div>
            <ReactEChartsCore option={hourlyTouOption(tou)} style={{ height: 220 }} />
          </Card>
        </Col>
      </Row>

      <Card title="分时电价明细" style={{ marginTop: 16 }} size="small">
        <Table
          columns={touTableColumns}
          dataSource={(tou.periods || []).map((p) => ({ ...p, key: p.period }))}
          pagination={false}
          size="small"
        />
      </Card>

      {hierarchy.children?.length > 0 && (
        <Card title={`下级空间能耗（${hierarchy.children.length} 个）`} style={{ marginTop: 16 }} size="small">
          <Table
            columns={childColumns}
            dataSource={hierarchy.children.map((c) => ({ ...c, key: c.id }))}
            pagination={false}
            size="small"
          />
        </Card>
      )}

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={16}><Card title="能耗趋势 (近7天)" size="small"><ReactEChartsCore option={chartOption} style={{ height: 280 }} /></Card></Col>
        <Col span={8}><Card title="能源结构" size="small"><ReactEChartsCore option={pieOption} style={{ height: 280 }} /></Card></Col>
      </Row>

      <Card title="碳排放概览" style={{ marginTop: 16 }} size="small" extra={
        <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>导出日报表</Button>
      }>
        <Row gutter={16}>
          <Col span={8}><Statistic title="直接碳排放 (市电)" value={co2Direct} suffix="kg" precision={0} /></Col>
          <Col span={8}><Statistic title="碳减排 (清洁能源)" value={co2Reduction} suffix="kg" valueStyle={{ color: '#52c41a' }} /></Col>
          <Col span={8}><Statistic title="净碳排放" value={co2Direct - co2Reduction} suffix="kg" valueStyle={{ color: '#faad14' }} /></Col>
        </Row>
        <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
          直接碳排放按电网平均排放因子 0.5703 kgCO₂/kWh 估算
        </div>
      </Card>
    </div>
  );
}
